import React, { useState, useCallback, useEffect } from 'react';
import './index.css';
import { x, y, z, w } from './utils/validation';

const App: React.FC = () => {
  const [archiveFile, setArchiveFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{message: string; type: 'success' | 'error' | 'warning' | null}>({message: '', type: null});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);
  const [softwareInfo, setSoftwareInfo] = useState<{name: string; version: string; certificate_number: string} | null>(null);
  const [softwareInfoError, setSoftwareInfoError] = useState<string | null>(null);
  const [isLoadingSoftwareInfo, setIsLoadingSoftwareInfo] = useState<boolean>(true);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  // Base URL is now hardcoded during build
  const baseUrl = process.env.REACT_APP_CUSTOMER_ORDERS_API_V2_BASE_URL || 'http://localhost:8000';

  // Fetch software info on component mount
  useEffect(() => {
    const fetchSoftwareInfo = async () => {
      try {
        console.log(`
      ( ͡° ͜ʖ ͡°)
      <Kappa>
    `);
        
        const response = await fetch(`${baseUrl}/v2/regulatory/nf525-software-info`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setSoftwareInfo({
            name: data.name || 'Taster NF525 Archive Checker',
            version: data.version || 'unknown',
            certificate_number: data.certificate_number || 'unknown',
          });
          setSoftwareInfoError(null);
        } else {
          throw new Error('Failed to fetch software info');
        }
      } catch (error) {
        console.error('Error fetching software info:', error);
        setSoftwareInfoError('Failed to contact verification server');
        setSoftwareInfo(null);
      } finally {
        setIsLoadingSoftwareInfo(false);
      }
    };
    
    fetchSoftwareInfo();
  }, [baseUrl]);

  const validateArchiveName = useCallback((filename: string): boolean => {
    return x(filename) as boolean;
  }, [x]);

  const validateSignatureName = useCallback((filename: string): boolean => {
    return y(filename) as boolean;
  }, [y]);

  const validateSignatureContent = useCallback(async (file: File): Promise<boolean> => {
    return z(file) as Promise<boolean>;
  }, [z]);

  const validateKeyContent = useCallback(async (file: File): Promise<boolean> => {
    return w(file) as Promise<boolean>;
  }, [w]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, fileType: 'archive' | 'signature' | 'key') => {
    const file = e.target.files?.[0];
    if (!file) return;

    let isValid = true;
    let errorMessage = '';

    if (fileType === 'archive') {
      if (!validateArchiveName(file.name)) {
        isValid = false;
        errorMessage = 'failed to parse name';
      }
    } else if (fileType === 'signature') {
      if (!validateSignatureName(file.name)) {
        isValid = false;
        errorMessage = 'failed to parse name';
      } else {
        const contentValid = await validateSignatureContent(file);
        if (!contentValid) {
          isValid = false;
          errorMessage = 'failed to parse content';
        }
      }
    } else if (fileType === 'key') {
      const contentValid = await validateKeyContent(file);
      if (!contentValid) {
        isValid = false;
        errorMessage = 'failed to parse content';
      }
    }

    if (isValid) {
      if (fileType === 'archive') setArchiveFile(file);
      else if (fileType === 'signature') setSignatureFile(file);
      else if (fileType === 'key') setKeyFile(file);
      setErrors(prev => ({ ...prev, [fileType]: '' }));
    } else {
      setErrors(prev => ({ ...prev, [fileType]: `${file.name} is not in taster format; ${errorMessage}` }));
    }
    
    // Check if there are any validation errors
    const hasErrors = Object.values({...errors, [fileType]: isValid ? '' : `${file.name} is not in taster format; ${errorMessage}`}).some(error => error !== '');
    setHasValidationErrors(hasErrors);
  }, [validateArchiveName, validateSignatureName, validateSignatureContent, validateKeyContent, errors]);

  const handleSubmit = useCallback(async () => {
    if (!archiveFile || !signatureFile || !keyFile) {
      setResult({message: 'Please select all three files before submitting.', type: 'error'});
      return;
    }

    if (errors.archive || errors.signature || errors.key) {
      setResult({message: 'Please fix all validation errors before submitting.', type: 'error'});
      return;
    }

    setIsLoading(true);
    setResult({message: 'Verifying files...', type: 'warning'});

    try {
      const formData = new FormData();
      formData.append('archive_file', archiveFile);
      formData.append('signature_file', signatureFile);
      formData.append('key_file', keyFile);

      const response = await fetch(`${baseUrl}/v2/regulatory/archives/external-verification`, {
        method: 'POST',
        body: formData,
      });

      if (response.status === 204) {
        setResult({message: `✅ Verification of archive ${archiveFile.name} successful!`, type: 'success'});
      } else if (response.status === 410) {
        try {
          const errorData = await response.json();
          const correlationId = errorData.correlation_id || 'unknown';
          setResult({message: `❌ Verification of archive ${archiveFile.name} failed. Correlation ID: ${correlationId}. Copy this information to transmit to Taster.`, type: 'error'});
        } catch (e) {
          setResult({message: `❌ Verification of archive ${archiveFile.name} failed.`, type: 'error'});
        }
      } else {
        setResult({message: '⚠️ Verification system problem. Result inconclusive.', type: 'warning'});
      }
    } catch (error) {
      setResult({message: '⚠️ Network error. Please check your connection.', type: 'error'});
    } finally {
      setIsLoading(false);
    }
  }, [archiveFile, signatureFile, keyFile, errors, baseUrl]);

  const allFilesSelected = archiveFile && signatureFile && keyFile;

  // Print Kappa ASCII art on startup
  useEffect(() => {
    console.log(`
⠀⠀⠀⠀⠀⠀⠀⠀⢀⣀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⢀⢤⢴⣿⣾⠷⣮⣿⣿⣯⣷⡶⣴⣢⢄⡀⠀⠀⠀
⠀⠀⢀⣴⣋⢫⡞⠑⢱⡎⣳⣭⣥⣧⣾⣿⢏⣽⣷⣼⣦⡀⠀
⢀⢔⢿⢿⠣⢱⣳⠛⣻⣴⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣷⠀
⢸⠜⢣⣙⣽⣾⣷⣿⣿⣿⣿⣿⡿⠿⠛⠙⠛⠿⣿⣿⣿⣿⠀
⣿⣼⣾⣿⣿⣿⣿⣿⣿⣿⣟⠋⠀⠀⠀⠀⠀⠀⠀⠻⣿⣿⡇
⣿⣿⣿⣿⣿⡿⣿⣟⠧⡀⠡⡀⠀⠀⠀⠀⠀⠀⠀⠀⠹⣿⡇
⢸⣿⣿⣿⣿⡿⠉⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⡇
⠈⠿⣿⣿⡟⠀⠀⠀⠠⠔⣒⣟⡋⠂⠀⢀⣼⣶⣶⣒⠧⠹⠀
⢸⡇⢚⢿⠁⠀⠀⠘⠚⠉⠩⠬⡽⠀⠀⠈⡌⠩⠭⠉⠁⠁⠀
⠀⢆⠯⠉⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠐⡀⠀⠀⠀⡀⠀
⠀⠀⠁⠁⠀⠀⠀⠀⠀⠀⠀⠀⠠⣀⡀⠀⢀⡤⠀⠀⣠⡇⠀
⠀⠀⠑⠖⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⡙⢻⠁⠀⠀⢀⠇⠀
⠀⠀⠀⠀⢄⠀⠀⠀⠀⠀⠀⠒⠒⠒⠒⠒⠿⠋⠁⢀⡜⠀⠀
⠀⠀⠀⠀⠀⠠⡀⠀⠀⠀⠀⠀⠀⠀⠈⠫⣀⠀⣮⠏⠀⠀⠀
⠀⠀⠀⠀⠀⠀⠀⠑⠤⢀⡀⠀⡀⠀⠀⠀⣀⠼⠁⠀⠀⠀⠀
⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⠉⠉⠉⠉⠉⠁⠀⠀⠀⠀⠀⠀
    `);
  }, []);

  return (
    <div className="desktop">
      <div className="window">
        <div className="title-bar">
          <div className="title">🦍 Taster NF525 Archive Verification Utility 🗿</div>
          <div className="window-controls">
            <a href="https://www.youtube.com/watch?v=fPll3mpO9oE" className="control-button minimize-button" title="Minimize" target="_blank" rel="noopener noreferrer"></a>
            <a href="https://www.youtube.com/watch?v=EO9x0y5lqD0" className="control-button maximize-button" title="Maximize" target="_blank" rel="noopener noreferrer"></a>
            <a href="https://www.youtube.com/watch?v=7VnFjm1PEHQ" className="control-button close-button" title="Close" target="_blank" rel="noopener noreferrer"></a>
          </div>
        </div>
        
        <div className="menu-bar">
          <div 
            className="menu-item"
            onMouseEnter={() => setActiveMenu('file')}
            onMouseLeave={() => setActiveMenu(null)}
          >
            File
            {activeMenu === 'file' && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => setActiveMenu('file')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <img src="/duck.gif" alt="Wow !" style={{width: '100%', height: '150px', objectFit: 'contain'}} />
              </div>
            )}
          </div>
          <div 
            className="menu-item"
            onMouseEnter={() => setActiveMenu('edit')}
            onMouseLeave={() => setActiveMenu(null)}
          >
            Edit
            {activeMenu === 'edit' && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => setActiveMenu('edit')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <img src="/fast-dude.gif" alt="Groundbreaking" style={{width: '100%', height: '150px', objectFit: 'contain'}} />
              </div>
            )}
          </div>
          <div 
            className="menu-item"
            onMouseEnter={() => setActiveMenu('view')}
            onMouseLeave={() => setActiveMenu(null)}
          >
            View
            {activeMenu === 'view' && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => setActiveMenu('view')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <img src="/sesame-street.gif" alt="Verifications underway" style={{width: '100%', height: '150px', objectFit: 'contain'}} />
              </div>
            )}
          </div>
          <div 
            className="menu-item"
            onMouseEnter={() => setActiveMenu('help')}
            onMouseLeave={() => setActiveMenu(null)}
          >
            Help
            {activeMenu === 'help' && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => setActiveMenu('help')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <img src="/arsene.gif" alt="Where files" style={{width: '100%', height: '150px', objectFit: 'contain'}} />
              </div>
            )}
          </div>
        </div>

        <div className="window-content">
          <div className="upload-form">
            <div className="form-group">
              <label className="form-label">
                <span className="icon file-icon"></span>
                📦 Archive (ZIP)
              </label>
              <input
                type="file"
                className="file-input"
                id="archive-upload"
                accept=".zip"
                onChange={(e) => handleFileChange(e, 'archive')}
              />
              {archiveFile && (
                <div className="file-info file-selected">
                  ✓ {archiveFile.name}
                </div>
              )}
              {errors.archive && (
                <div className="file-info error-message">
                  ❌ {errors.archive}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="icon file-icon"></span>
                📄 Signature (TXT)
              </label>
              <input
                type="file"
                className="file-input"
                id="signature-upload"
                accept=".txt"
                onChange={(e) => handleFileChange(e, 'signature')}
              />
              {signatureFile && (
                <div className="file-info file-selected">
                  ✓ {signatureFile.name}
                </div>
              )}
              {errors.signature && (
                <div className="file-info error-message">
                  ❌ {errors.signature}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="icon file-icon"></span>
                🔑 Public Key (PEM)
              </label>
              <input
                type="file"
                className="file-input"
                id="key-upload"
                accept=".pem"
                onChange={(e) => handleFileChange(e, 'key')}
              />
              {keyFile && (
                <div className="file-info file-selected">
                  ✓ {keyFile.name}
                </div>
              )}
              {errors.key && (
                <div className="file-info error-message">
                  ❌ {errors.key}
                </div>
              )}
            </div>

            <div className="submit-container">
              <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={!allFilesSelected || hasValidationErrors || isLoading || !!softwareInfoError}
              >
                {isLoading ? 'Verifying...' : 'Verify Files'}
              </button>
              {hasValidationErrors && (
                <div className="error-message" style={{marginLeft: '10px', display: 'flex', alignItems: 'center'}}>
                  ❌ File name / contents check failed, cannot proceed with verification
                </div>
              )}
              {softwareInfoError && (
                <div className="error-message" style={{marginLeft: '10px', display: 'flex', alignItems: 'center'}}>
                  ❌ {softwareInfoError}
                </div>
              )}
            </div>

            {result.message && (
              <div className={`result-message ${result.type}`}>
                {result.message}
              </div>
            )}
          </div>
        </div>

        <div className="status-bar">
          <div>
            {isLoadingSoftwareInfo ? 'Loading software info...' : 
             softwareInfoError ? softwareInfoError : 
             softwareInfo ? `${softwareInfo.name} version ${softwareInfo.version}, certificate ${softwareInfo.certificate_number}` : 
             'Taster NF525 Archive Checker'}
          </div>
          <div>
            Taster NF525 Archive Checker v1.0
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
